import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import clsx from 'clsx';
import React from 'react';
import type { PopoverProps } from './types';
import { usePopoverPanel } from './usePopover';

/**
 * Popover — v2 molecule.
 *
 * Panel flotante sobre MUI `Popper`. Sustituye al antiguo Dropdown v1 (ya
 * eliminado), con clases propias `popover__lib` / `popover__panel`. El wrapper
 * `div.dropdown` del v1 se elimina: Popper se renderiza en un portal al final
 * de `body`, así que ese div quedaba siempre vacío y sin estilos.
 *
 * Ámbito: panel flotante genérico (posicionado, click-away, Escape, cierre al
 * tabular fuera y devolución de foco). NO implementa el patrón `menu` (flechas,
 * type-ahead, `role="menu"`), que se aborda en TAN-7301.
 *
 * Consumo canónico: hook `usePopover` + `getTriggerProps()`. También funciona
 * de forma autónoma con `anchorEl`/`open` gestionados externamente (patrón v1).
 */
function Popover({
  anchorEl,
  ariaLabelledBy,
  children,
  className,
  container,
  handleClose,
  modifiers,
  open,
  placement,
  role,
  shouldCloseOnTabLeave = false,
  shouldCloseOnTabOut = false,
  shouldFocusOnOpen = true,
}: Readonly<PopoverProps>): JSX.Element {
  // `container` explícito (p. ej. AddModuleMenuButton, TAN-10189) manda; si no llega, el del diálogo que contiene el ancla (TAN-11141).
  const { container: dialogContainer, getGrowStyle, onClickAway, onPanelKeyDown, panelRef, transitionTimeout } = usePopoverPanel({
    anchorEl,
    handleClose,
    open,
    shouldCloseOnTabLeave,
    shouldCloseOnTabOut,
    shouldFocusOnOpen,
  });

  return (
    <Popper
      anchorEl={anchorEl}
      className={clsx(className, 'popover__lib')}
      container={container ?? dialogContainer}
      modifiers={modifiers}
      open={open}
      placement={placement}
      role={undefined}
      transition
    >
      {({ TransitionProps, placement: resolvedPlacement }) => (
        <Grow {...TransitionProps} style={getGrowStyle(resolvedPlacement)} timeout={transitionTimeout}>
          <Paper>
            <ClickAwayListener onClickAway={onClickAway}>
              {/* role="none" (sinónimo ARIA de presentation): el panel es un contenedor sin semántica
                  propia — el contenido aporta la suya (menú/formulario). El rol es necesario para que
                  el `onKeyDown` (delegación del cierre al tabular, no un control interactivo) no se
                  interprete como elemento interactivo no nativo. Mismo patrón que OptionsDropdownMenu.
                  Si llega `role` (TAN-11122), la semántica y el nombre van en un envoltorio interior sin
                  manejadores: ponerlos en el panel con un rol dinámico lo marca Sonar (S6848). */}
              <div className="popover__panel" onKeyDown={onPanelKeyDown} ref={panelRef} role="none" tabIndex={-1}>
                {role ? (
                  <div aria-labelledby={ariaLabelledBy} className="popover__content" role={role}>
                    {children}
                  </div>
                ) : (
                  children
                )}
              </div>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
}

export default Popover;
